import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  defer,
  finalize,
  map,
  Observable,
  of,
  Subscription,
  tap,
} from 'rxjs';

import {
  ArcaCatalogItem,
  ArcaCurrencyType,
  ArcaInvoiceFormCatalogResponse,
  ArcaInvoiceIssuerData,
  ArcaInvoiceLineItem,
  ArcaInvoiceLineItemType,
  ArcaInvoiceSourceDraftResponse,
  ArcaInvoiceSourceOption,
  ArcaInvoiceSourceType,
  ArcaInvoiceValidationPayload,
  ArcaInvoiceValidationResponse,
  ArcaReceiverVatCondition,
  ArcaSalesPoint,
  ArcaVoucherType,
} from '@interfaces/ArcaInvoiceForm';
import { ApiResponse } from '@interfaces/httpResponses';
import { environment } from 'environments/environment';
import { ApiErrorAdapter } from '../shared/handlers/api-error-adapter.service';
import { PermissionsService } from '../shared/permissions.service';
import {
  blockingLoaderHeaders,
  skipLoaderHeaders,
} from 'app/core/interceptors/loader.interceptor';
import { BusyTracker } from 'app/shared/utils/busy-indicator';

@Injectable({
  providedIn: 'root',
})
export class ArcaElectronicBillingService {
  private static readonly ALLOWED_VOUCHER_TYPE_IDS = new Set([1, 6, 11]);

  private readonly baseUrl = `${environment.apiUrl}user/arca/electronic-billing`;

  private activeRequests = 0;
  private catalogLoaded = false;

  private catalogRequestSubscription?: Subscription;
  private sourceDraftRequestSubscription?: Subscription;
  private sourceOptionsRequestSubscription?: Subscription;

  private readonly issuerSubject =
    new BehaviorSubject<ArcaInvoiceIssuerData | null>(null);

  private readonly salesPointsSubject = new BehaviorSubject<ArcaSalesPoint[]>(
    [],
  );

  private readonly voucherTypesSubject = new BehaviorSubject<ArcaVoucherType[]>(
    [],
  );

  private readonly conceptTypesSubject = new BehaviorSubject<ArcaCatalogItem[]>(
    [],
  );

  private readonly documentTypesSubject = new BehaviorSubject<
    ArcaCatalogItem[]
  >([]);

  private readonly aliquotTypesSubject = new BehaviorSubject<ArcaCatalogItem[]>(
    [],
  );

  private readonly currencyTypesSubject = new BehaviorSubject<
    ArcaCurrencyType[]
  >([]);

  private readonly taxTypesSubject = new BehaviorSubject<ArcaCatalogItem[]>([]);

  private readonly receiverVatConditionsSubject = new BehaviorSubject<
    ArcaReceiverVatCondition[]
  >([]);

  private readonly invoiceSourceDraftSubject =
    new BehaviorSubject<ArcaInvoiceSourceDraftResponse | null>(null);

  private readonly invoiceProductItemsSubject = new BehaviorSubject<
    ArcaInvoiceLineItem[]
  >([]);

  private readonly invoiceServiceItemsSubject = new BehaviorSubject<
    ArcaInvoiceLineItem[]
  >([]);

  private readonly invoiceSourceOptionsSubject = new BehaviorSubject<
    ArcaInvoiceSourceOption[]
  >([]);

  private readonly invoiceValidationResultSubject =
    new BehaviorSubject<ArcaInvoiceValidationResponse | null>(null);

  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);

  private electronicBillingIntegrationStatusLoaded = false;
  private electronicBillingIntegrationStatusLoading = false;

  private readonly electronicBillingIntegrationEnabledSubject =
    new BehaviorSubject<boolean>(false);

  readonly electronicBillingIntegrationEnabled$ =
    this.electronicBillingIntegrationEnabledSubject.asObservable();

  get electronicBillingIntegrationEnabled(): boolean {
    return this.electronicBillingIntegrationEnabledSubject.value;
  }

  private readonly sourceOptionsTracker = new BusyTracker();
  /** Spinner for the invoiceable sale/order typeahead (not the global overlay). */
  readonly sourceOptionsBusy$ = this.sourceOptionsTracker.indicator$();

  readonly issuer$ = this.issuerSubject.asObservable();
  readonly salesPoints$ = this.salesPointsSubject.asObservable();
  readonly voucherTypes$ = this.voucherTypesSubject.asObservable();
  readonly conceptTypes$ = this.conceptTypesSubject.asObservable();
  readonly documentTypes$ = this.documentTypesSubject.asObservable();
  readonly aliquotTypes$ = this.aliquotTypesSubject.asObservable();
  readonly currencyTypes$ = this.currencyTypesSubject.asObservable();
  readonly taxTypes$ = this.taxTypesSubject.asObservable();

  readonly receiverVatConditions$ =
    this.receiverVatConditionsSubject.asObservable();

  readonly invoiceSourceDraft$ = this.invoiceSourceDraftSubject.asObservable();

  readonly invoiceProductItems$ =
    this.invoiceProductItemsSubject.asObservable();

  readonly invoiceServiceItems$ =
    this.invoiceServiceItemsSubject.asObservable();

  readonly invoiceSourceOptions$ =
    this.invoiceSourceOptionsSubject.asObservable();

  readonly invoiceValidationResult$ =
    this.invoiceValidationResultSubject.asObservable();

  readonly isLoading$ = this.isLoadingSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly apiErrors: ApiErrorAdapter,
    private readonly permissions: PermissionsService,
  ) {}

  public loadInvoiceFormCatalog(options: { force?: boolean } = {}): void {
    if (this.catalogLoaded && !options.force) {
      return;
    }

    this.catalogRequestSubscription?.unsubscribe();

    this.catalogRequestSubscription =
      this.requestPayload<ArcaInvoiceFormCatalogResponse | null>(
        this.http.get<ApiResponse<ArcaInvoiceFormCatalogResponse>>(
          `${this.baseUrl}/invoice-form/catalog`,
          { headers: skipLoaderHeaders() },
        ),
        'No se pudieron cargar los datos de facturación de ARCA.',
        null,
      ).subscribe((catalog) => {
        if (!catalog) {
          return;
        }

        this.catalogLoaded = true;
        this.setCatalog(catalog);
      });
  }

  public loadInvoiceSourceDraft(params: {
    sourceType: ArcaInvoiceSourceType;
    sourceId: string;
  }): void {
    this.cancelSourceDraftRequest();
    this.resetSelectedSourceState();

    this.sourceDraftRequestSubscription =
      this.requestPayload<ArcaInvoiceSourceDraftResponse | null>(
        this.http.get<ApiResponse<ArcaInvoiceSourceDraftResponse>>(
          `${this.baseUrl}/invoice-form/source/${params.sourceType}/${params.sourceId}`,
          { headers: skipLoaderHeaders() },
        ),
        'No se pudieron cargar los productos y servicios a facturar.',
        null,
      ).subscribe((draft) => {
        if (!draft) {
          return;
        }

        this.setInvoiceSourceDraft(draft);
      });
  }

  public loadInvoiceSourceOptions(search = ''): void {
    this.cancelSourceOptionsRequest();

    this.sourceOptionsRequestSubscription = this.requestPayload<
      ArcaInvoiceSourceOption[]
    >(
      this.sourceOptionsTracker.track(
        this.http.get<ApiResponse<ArcaInvoiceSourceOption[]>>(
          `${this.baseUrl}/invoice-form/sources`,
          {
            params: {
              search,
              limit: '10',
            },
            headers: skipLoaderHeaders(),
          },
        ),
      ),
      'No se pudieron cargar las ventas u órdenes facturables.',
      [],
    ).subscribe((sources) => {
      this.invoiceSourceOptionsSubject.next(sources);
    });
  }

  public validateInvoiceDraft(
    payload: ArcaInvoiceValidationPayload,
  ): Observable<ArcaInvoiceValidationResponse | null> {
    this.invoiceValidationResultSubject.next(null);

    return this.requestPayload<ArcaInvoiceValidationResponse | null>(
      this.http.post<ApiResponse<ArcaInvoiceValidationResponse>>(
        `${this.baseUrl}/invoice-form/validate`,
        payload,
        {
          headers: blockingLoaderHeaders({
            message: 'Validando factura...',
            money: true,
          }),
        },
      ),
      'No se pudo validar la factura antes de emitir.',
      null,
    ).pipe(
      tap((validationResult) => {
        this.invoiceValidationResultSubject.next(validationResult);
      }),
    );
  }

  public loadElectronicBillingIntegrationStatus(
    options: { force?: boolean } = {},
  ): void {
    const shouldSkipRequest =
      !options.force &&
      (this.electronicBillingIntegrationStatusLoaded ||
        this.electronicBillingIntegrationStatusLoading);

    if (shouldSkipRequest) {
      return;
    }

    // The catalog requires `invoices:issue`: without it there is nothing to
    // show and the API answers 403. Not marked as loaded, so a later session
    // with the permission still asks.
    if (!this.permissions.can('invoices:issue')) {
      this.electronicBillingIntegrationEnabledSubject.next(false);
      return;
    }

    this.electronicBillingIntegrationStatusLoading = true;

    this.http
      .get<ApiResponse<ArcaInvoiceFormCatalogResponse>>(
        `${this.baseUrl}/invoice-form/catalog`,
        { headers: skipLoaderHeaders() },
      )
      .pipe(
        map((response) => {
          const catalog = response.payload;
          const hasIntegration = this.hasValidInvoiceFormCatalog(catalog);

          if (hasIntegration) {
            this.catalogLoaded = true;
            this.setCatalog(catalog);
          }

          return hasIntegration;
        }),
        catchError(() => of(false)),
        finalize(() => {
          this.electronicBillingIntegrationStatusLoading = false;
          this.electronicBillingIntegrationStatusLoaded = true;
        }),
      )
      .subscribe((enabled) => {
        this.electronicBillingIntegrationEnabledSubject.next(enabled);
      });
  }

  public clearSelectedInvoiceSource(): void {
    this.cancelSourceDraftRequest();
    this.resetSelectedSourceState();
    this.clearError();
  }

  public clearInvoiceSourceOptions(): void {
    this.cancelSourceOptionsRequest();
    this.invoiceSourceOptionsSubject.next([]);
  }

  public clearInvoiceWorkflowState(): void {
    this.cancelInvoiceWorkflowRequests();

    this.resetSelectedSourceState();
    this.invoiceSourceOptionsSubject.next([]);
    this.invoiceValidationResultSubject.next(null);
    this.clearError();
  }

  public clearCatalogCache(): void {
    this.catalogRequestSubscription?.unsubscribe();
    this.catalogLoaded = false;
    this.electronicBillingIntegrationStatusLoaded = false;
    this.electronicBillingIntegrationStatusLoading = false;
    this.electronicBillingIntegrationEnabledSubject.next(false);
    this.clearCatalog();
  }

  private requestPayload<T>(
    request$: Observable<ApiResponse<T>>,
    errorMessage: string,
    fallback: T,
  ): Observable<T> {
    return defer(() => {
      this.startLoading();
      this.clearError();

      return request$.pipe(
        map((response) => response.payload),
        catchError((error: unknown) => {
          this.setError(this.apiErrors.from(error, errorMessage).message);

          return of(fallback);
        }),
        finalize(() => this.stopLoading()),
      );
    });
  }

  private setCatalog(catalog: ArcaInvoiceFormCatalogResponse): void {
    this.issuerSubject.next(catalog.issuer);

    this.salesPointsSubject.next(
      catalog.catalogs.salesPoints.resultGet?.ptoVenta ?? [],
    );

    this.voucherTypesSubject.next(
      this.filterAllowedVoucherTypes(
        catalog.catalogs.voucherTypes.resultGet?.cbteTipo ?? [],
      ),
    );

    this.conceptTypesSubject.next(
      catalog.catalogs.conceptTypes.resultGet?.conceptoTipo ?? [],
    );

    this.documentTypesSubject.next(
      catalog.catalogs.documentTypes.resultGet?.docTipo ?? [],
    );

    this.aliquotTypesSubject.next(
      catalog.catalogs.aliquotTypes.resultGet?.ivaTipo ?? [],
    );

    this.currencyTypesSubject.next(
      catalog.catalogs.currencyTypes.resultGet?.moneda ?? [],
    );

    this.taxTypesSubject.next(
      catalog.catalogs.taxTypes.resultGet?.tributoTipo ?? [],
    );

    this.receiverVatConditionsSubject.next(
      catalog.catalogs.receiverVatConditions.resultGet?.condicionIvaReceptor ??
        [],
    );
  }

  public previewInvoicePdf(
    payload: ArcaInvoiceValidationPayload,
  ): Observable<Blob | null> {
    return defer(() => {
      this.startLoading();
      this.clearError();

      return this.http
        .post(`${this.baseUrl}/invoice-form/pdf`, payload, {
          responseType: 'blob',
          headers: blockingLoaderHeaders({
            message: 'Generando previsualización...',
          }),
        })
        .pipe(
          catchError((error: unknown) => {
            this.setError(
              this.apiErrors.from(
                error,
                'No se pudo generar la previsualización de la factura.',
              ).message,
            );

            return of(null);
          }),
          finalize(() => this.stopLoading()),
        );
    });
  }

  private setInvoiceSourceDraft(draft: ArcaInvoiceSourceDraftResponse): void {
    const productItems = draft.items.filter(
      (item) => item.type === ArcaInvoiceLineItemType.PRODUCT,
    );

    const serviceItems = draft.items.filter(
      (item) => item.type === ArcaInvoiceLineItemType.SERVICE,
    );

    this.invoiceSourceDraftSubject.next(draft);
    this.invoiceProductItemsSubject.next(productItems);
    this.invoiceServiceItemsSubject.next(serviceItems);
  }

  private resetSelectedSourceState(): void {
    this.invoiceSourceDraftSubject.next(null);
    this.invoiceProductItemsSubject.next([]);
    this.invoiceServiceItemsSubject.next([]);
    this.invoiceValidationResultSubject.next(null);
  }

  private clearCatalog(): void {
    this.issuerSubject.next(null);
    this.salesPointsSubject.next([]);
    this.voucherTypesSubject.next([]);
    this.conceptTypesSubject.next([]);
    this.documentTypesSubject.next([]);
    this.aliquotTypesSubject.next([]);
    this.currencyTypesSubject.next([]);
    this.taxTypesSubject.next([]);
    this.receiverVatConditionsSubject.next([]);
  }

  private filterAllowedVoucherTypes(
    voucherTypes: ArcaVoucherType[],
  ): ArcaVoucherType[] {
    return voucherTypes.filter((voucherType) =>
      ArcaElectronicBillingService.ALLOWED_VOUCHER_TYPE_IDS.has(voucherType.id),
    );
  }

  private cancelInvoiceWorkflowRequests(): void {
    this.cancelSourceDraftRequest();
    this.cancelSourceOptionsRequest();
  }

  private cancelSourceDraftRequest(): void {
    this.sourceDraftRequestSubscription?.unsubscribe();
    this.sourceDraftRequestSubscription = undefined;
  }

  private cancelSourceOptionsRequest(): void {
    this.sourceOptionsRequestSubscription?.unsubscribe();
    this.sourceOptionsRequestSubscription = undefined;
  }

  private clearError(): void {
    this.errorSubject.next(null);
  }

  private setError(message: string): void {
    this.errorSubject.next(message);
  }

  private startLoading(): void {
    this.activeRequests += 1;

    if (!this.isLoadingSubject.value) {
      this.isLoadingSubject.next(true);
    }
  }

  private stopLoading(): void {
    this.activeRequests = Math.max(this.activeRequests - 1, 0);

    if (this.activeRequests === 0 && this.isLoadingSubject.value) {
      this.isLoadingSubject.next(false);
    }
  }

  private hasValidInvoiceFormCatalog(
    catalog: ArcaInvoiceFormCatalogResponse | null | undefined,
  ): catalog is ArcaInvoiceFormCatalogResponse {
    return Boolean(
      catalog?.issuer?.cuit &&
        catalog.catalogs?.salesPoints?.resultGet?.ptoVenta?.length &&
        catalog.catalogs?.voucherTypes?.resultGet?.cbteTipo?.length,
    );
  }
}
